import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography, Avatar, Button, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import { chatAPI } from '../services/api';
import { useChat } from '../contexts/ChatContext';
import ChatList, { chatInitials } from '../components/common/ChatList';
import ProjectChat from '../components/common/ProjectChat';
import PushBanner from '../components/common/PushBanner';

/**
 * Todos los chats de proyecto en una pantalla. En el escritorio, al estilo
 * WhatsApp Web: la lista a la izquierda y la conversacion a la derecha. En el
 * movil, una cosa o la otra: la lista, y al abrir un chat, la conversacion a
 * pantalla completa con su flecha para volver. Es el mismo chat que el de la
 * pestana Chat de cada proyecto y el de la app de los tecnicos.
 *
 * El chat abierto va en la URL (?p=<id>) para poder enlazarlo, para que recargar
 * no lo cierre y para que el boton atras del movil vuelva a la lista.
 */
export default function Chats() {
  const navigate = useNavigate();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params, setParams] = useSearchParams();
  const { refreshUnread } = useChat();
  const selectedId = Number(params.get('p')) || null;
  const [project, setProject] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setProject(null);
    if (!selectedId) return;
    chatAPI.messages(selectedId, { limit: 1 })
      .then((res) => setProject(res.data.project))
      .catch(() => setProject(null));
  }, [selectedId]);

  const open = (id) => setParams({ p: String(id) });
  const onActivity = useCallback(() => {
    setRefreshKey((k) => k + 1);
    refreshUnread();
  }, [refreshUnread]);

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 0.5, md: 2 }, py: 1,
               bgcolor: '#f0f2f5', borderBottom: '1px solid #e0e0e0' }}>
      {mobile && (
        <IconButton onClick={() => navigate('/chats')} aria-label="Volver"><ArrowBackIcon /></IconButton>
      )}
      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
        {chatInitials(project?.name)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} noWrap>{project?.name || '...'}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {[project?.code, project?.client_name].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      {mobile ? (
        <IconButton onClick={() => navigate(`/projects/${selectedId}`)} aria-label="Abrir proyecto">
          <OpenInNewIcon />
        </IconButton>
      ) : (
        <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/projects/${selectedId}`)}>
          Abrir proyecto
        </Button>
      )}
    </Box>
  );

  const list = (
    <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0, borderRight: { md: '1px solid #e0e0e0' },
               overflowY: 'auto', bgcolor: '#fff' }}>
      <Typography variant="h6" fontWeight="bold" sx={{ px: 2, pt: 1.5 }}>Chats</Typography>
      <PushBanner />
      <ChatList onOpenChat={open} selectedId={selectedId} refreshKey={refreshKey} />
    </Box>
  );

  const conversation = selectedId ? (
    <ProjectChat key={selectedId} projectId={selectedId} onActivity={onActivity} header={header} />
  ) : (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
               justifyContent: 'center', bgcolor: '#f0f2f5', color: 'text.secondary', gap: 1 }}>
      <ChatIcon sx={{ fontSize: 64, opacity: 0.3 }} />
      <Typography>Elige un chat de la lista</Typography>
      <Typography variant="body2">
        Son los mismos que ven los tecnicos en la app del movil.
      </Typography>
    </Box>
  );

  return (
    <Paper sx={{
      display: 'flex', overflow: 'hidden', minHeight: 360,
      // 100dvh: el alto real visible, que encoge al abrir el teclado del movil
      height: { xs: 'calc(100vh - 80px)', md: 'calc(100vh - 112px)' },
      '@supports (height: 100dvh)': { height: { xs: 'calc(100dvh - 80px)', md: 'calc(100vh - 112px)' } },
    }}>
      {mobile ? (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          {selectedId ? <Box sx={{ flex: 1, minWidth: 0 }}>{conversation}</Box> : list}
        </Box>
      ) : (
        <>
          {list}
          <Box sx={{ flex: 1, minWidth: 0 }}>{conversation}</Box>
        </>
      )}
    </Paper>
  );
}
